/**
 * Agent Context Provider
 * 
 * Provides global agent state management and shared functionality
 * across all components in the solar farm application.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { agentService, AgentResponse, AgentStatus } from '../services/AgentService';
import { ChatMessage } from '../types';
import { useAppContext } from './AppContext';

interface AgentContextType {
  // Connection state
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  status: AgentStatus | null;
  
  // Global chat state
  globalMessages: ChatMessage[];
  unreadCount: number;
  
  // Actions
  sendGlobalMessage: (message: string) => Promise<void>;
  clearGlobalMessages: () => void;
  markAsRead: () => void;
  
  // Agent capabilities
  takeScreenshot: () => Promise<string | null>;
  analyzeCurrentView: (prompt?: string) => Promise<any>;
  executeUIAction: (action: string, params: any) => Promise<any>;
  
  // Connection management
  reconnect: () => Promise<void>;
  disconnect: () => void;
  
  // Notifications
  showNotification: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
}

interface Notification {
  id: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: Date;
}

const AgentContext = createContext<AgentContextType | undefined>(undefined);

interface AgentProviderProps {
  children: ReactNode;
  enableAutoReconnect?: boolean;
  notificationDuration?: number;
}

export const AgentProvider: React.FC<AgentProviderProps> = ({
  children,
  enableAutoReconnect = true,
  notificationDuration = 5000
}) => {
  const appContext = useAppContext();
  
  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<AgentStatus | null>(null);
  
  // Global chat state
  const [globalMessages, setGlobalMessages] = useState<ChatMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Notifications
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Initialize agent service - simplified to avoid infinite polling
  useEffect(() => {
    const initializeAgent = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        await agentService.initialize();
        
        // Don't call getAgentStatus() immediately - assume connected after init
        setIsConnected(true);
        
        // Send initial context
        await agentService.updateSessionContext(appContext.state);

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize agent';
        setError(errorMessage);
        setIsConnected(false);
        
        showNotification('AI Assistant failed to connect', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAgent();
  }, [enableAutoReconnect]); // Removed appContext.state dependency

  // Set up real-time message handling
  useEffect(() => {
    const unsubscribeMessages = agentService.onMessage((response: AgentResponse) => {
      const assistantMessage: ChatMessage = {
        id: `global-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
        actions: response.actions_taken.map(action => ({
          type: mapActionType(action.action),
          payload: action.details
        }))
      };

      setGlobalMessages(prev => [...prev, assistantMessage]);
      setUnreadCount(prev => prev + 1);
      
      // Execute UI updates from agent response
      executeAgentActions(response);
      
      // Show notification for important messages
      if (response.requires_user_input || response.error) {
        showNotification(
          response.error || 'Agent requires your attention',
          response.error ? 'error' : 'info'
        );
      }
    });

    const unsubscribeStatus = agentService.onStatusChange((newStatus: AgentStatus) => {
      setStatus(newStatus);
      const connected = newStatus.status === 'ready';
      
      if (connected !== isConnected) {
        setIsConnected(connected);
        showNotification(
          `AI Assistant ${connected ? 'connected' : 'disconnected'}`,
          connected ? 'success' : 'warning'
        );
      }
    });

    return () => {
      unsubscribeMessages();
      unsubscribeStatus();
    };
  }, [isConnected]);

  // Update agent context when app state changes - disabled to prevent excessive calls
  // Context will be sent with each message instead
  // useEffect(() => {
  //   if (isConnected) {
  //     agentService.updateSessionContext(appContext.state).catch(console.warn);
  //   }
  // }, [isConnected, appContext.state]);

  // Execute actions from agent responses
  const executeAgentActions = useCallback(async (response: AgentResponse) => {
    // Handle UI updates
    if (response.ui_updates.navigate_to) {
      appContext.setActiveTab(response.ui_updates.navigate_to);
    }
    
    if (response.ui_updates.highlight) {
      appContext.setSelectedComponents(response.ui_updates.highlight);
    }
    
    if (response.ui_updates.filter) {
      appContext.updateFilter(response.ui_updates.filter);
    }

    // Handle other actions
    for (const action of response.actions_taken) {
      switch (action.action) {
        case 'open-tab':
          if (action.details) {
            appContext.setActiveTab(action.details);
          }
          break;
        
        case 'screenshot':
          showNotification('Screenshot captured', 'success');
          break;
          
        case 'navigate':
          if (action.details?.tab) {
            appContext.setActiveTab(action.details.tab);
          }
          break;
          
        case 'highlight':
          if (action.details?.components) {
            appContext.setSelectedComponents(action.details.components);
          }
          break;
          
        default:
      }
    }
  }, [appContext]);

  // Send global message
  const sendGlobalMessage = useCallback(async (messageText: string) => {
    if (!messageText.trim()) return;

    const userMessage: ChatMessage = {
      id: `global-user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role: 'user',
      content: messageText,
      timestamp: new Date()
    };

    setGlobalMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const response = await agentService.sendMessage(messageText, {
        currentTab: appContext.state.activeTab || undefined,
        selectedComponents: appContext.state.selectedComponents,
        uiState: {
          selectedSite: appContext.state.selectedSite,
          filterCriteria: appContext.state.filterCriteria
        }
      });

      // Response will be handled by the message subscription
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      setError(errorMessage);
      
      const errorChatMessage: ChatMessage = {
        id: `global-error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        role: 'assistant',
        content: `Sorry, I encountered an error: ${errorMessage}. ${agentService.isConnected() ? 'Please try again.' : 'I\'m currently offline.'}`,
        timestamp: new Date()
      };
      
      setGlobalMessages(prev => [...prev, errorChatMessage]);
      showNotification(errorMessage, 'error');
      
    } finally {
      setIsLoading(false);
    }
  }, [appContext]);

  // Clear global messages
  const clearGlobalMessages = useCallback(() => {
    setGlobalMessages([]);
    setUnreadCount(0);
  }, []);

  // Mark messages as read
  const markAsRead = useCallback(() => {
    setUnreadCount(0);
  }, []);

  // Take screenshot
  const takeScreenshot = useCallback(async (): Promise<string | null> => {
    try {
      setIsLoading(true);
      const result = await agentService.takeScreenshot();
      showNotification('Screenshot captured successfully', 'success');
      return result.screenshot_path;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Screenshot failed';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Analyze current view
  const analyzeCurrentView = useCallback(async (prompt?: string) => {
    try {
      setIsLoading(true);
      const analysisPrompt = prompt || `Analyze the current ${appContext.state.activeTab || 'view'} and provide insights about what you see`;
      return await agentService.analyzeScreen(analysisPrompt);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Analysis failed';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [appContext.state.activeTab]);

  // Execute UI action
  const executeUIAction = useCallback(async (action: string, params: any) => {
    try {
      setIsLoading(true);
      const result = await agentService.controlUI(action, params);
      showNotification(`UI action "${action}" executed successfully`, 'success');
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `UI action "${action}" failed`;
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Reconnect to agent service
  const reconnect = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      await agentService.initialize();
      // Don't immediately call getAgentStatus to avoid request flood
      setIsConnected(true);
      
      showNotification('Successfully reconnected to AI Assistant', 'success');
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Reconnection failed';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Disconnect from agent service
  const disconnect = useCallback(() => {
    agentService.disconnect();
    setIsConnected(false);
    setStatus(null);
    setError(null);
    showNotification('Disconnected from AI Assistant', 'info');
  }, []);

  // Show notification
  const showNotification = useCallback((message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') => {
    const notification: Notification = {
      id: `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      message,
      type,
      timestamp: new Date()
    };

    setNotifications(prev => [notification, ...prev.slice(0, 4)]); // Keep only 5 most recent

    // Auto-remove notification after duration
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, notificationDuration);
  }, [notificationDuration]);

  // Initialize welcome message
  useEffect(() => {
    if (globalMessages.length === 0) {
      const welcomeMessage: ChatMessage = {
        id: 'welcome-global',
        role: 'assistant',
        content: isConnected 
          ? '🌟 AI Assistant ready! I can help you analyze your solar farm, control the interface, and provide insights. Try asking me to analyze the current view or take a screenshot!'
          : '🔄 AI Assistant in offline mode. Basic functionality available. Some features require connection to the backend service.',
        timestamp: new Date()
      };
      
      setGlobalMessages([welcomeMessage]);
    }
  }, [globalMessages.length, isConnected]);

  const contextValue: AgentContextType = {
    isConnected,
    isLoading,
    error,
    status,
    globalMessages,
    unreadCount,
    sendGlobalMessage,
    clearGlobalMessages,
    markAsRead,
    takeScreenshot,
    analyzeCurrentView,
    executeUIAction,
    reconnect,
    disconnect,
    showNotification
  };

  return (
    <AgentContext.Provider value={contextValue}>
      {children}
      
      {/* Notification Toast Container */}
      <div className="fixed top-4 right-4 z-50 space-y-2 pointer-events-none">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`p-3 rounded-lg shadow-lg max-w-sm pointer-events-auto transform transition-all duration-300 ${
              notification.type === 'error' ? 'bg-accent-red text-white' :
              notification.type === 'warning' ? 'bg-yellow-600 text-white' :
              notification.type === 'success' ? 'bg-green-600 text-white' :
              'bg-blue-600 text-white'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm">{notification.message}</span>
              <button
                onClick={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
                className="ml-2 text-white hover:text-gray-200"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
    </AgentContext.Provider>
  );
};

export const useAgentContext = () => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgentContext must be used within AgentProvider');
  }
  return context;
};

// Helper function to map backend action types to frontend types
function mapActionType(backendAction: string): 'open-tab' | 'filter' | 'navigate' {
  switch (backendAction) {
    case 'open-tab':
    case 'navigate-tab':
    case 'switch-tab':
      return 'open-tab';
    case 'filter':
    case 'search':
    case 'query':
      return 'filter';
    case 'navigate':
    case 'goto':
    case 'move':
      return 'navigate';
    default:
      return 'navigate';
  }
}

export default AgentProvider;